import { markRaw } from "vue";
import { SENSOR_SIZE, TEST_COMPANIES, TEST_MANAUL_UPLOAD_PROJECTS } from "@/constants";
import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import pinpointSvg from "@/assets/icons/pinpoint.svg";
import { ElMessage } from "element-plus";
import { trackEvent } from "@/utils/analytics.js";

export const initializeGalleryMode = async function (imageSize, imagesCount) {
  if (!this.thumbnailsAvailable) {
    return this.restoreDefaultCursor();
  }

  if (!imageSize) {
    return this.restoreDefaultCursor();
  }

  this.originalImageWidth = imageSize.width;
  this.originalImageHeight = imageSize.height;

  if (!this.cameraMesh) {
    await this.fetchCameras();
  }

  // Source is set by setupCameraMesh based on the BE priority chain:
  // "transform" → new CameraTransform path, "csv" → legacy Camera/CSV path.
  if (this.cameraSource === "transform") {
    if (!this.camerasLoadedWithImages) {
      if (!this.runCompletenessTest(imagesCount)) {
        return this.useLimitiedGalleryMode(imagesCount);
      }
    }
    if (this.camerasFrustums.length === 0) {
      this.camerasFrustums = this.cameraTransforms.map((row, index) =>
        this.createFrustumFromTransform(row, index)
      );
    }
    document.addEventListener("click", this.selectPointOnModel, false);
    if (this.workerInitialized === null) this.initializeCameraWorkers();
    this.changeCursorToPointer();
    return;
  }

  if (this.csvAvailable) {
    if (!this.camerasLoadedWithImages) {
      const completenessTestPassed = this.runCompletenessTest(imagesCount);
      if (!completenessTestPassed)
        return this.useLimitiedGalleryMode(imagesCount);
    }

    if (this.camerasFrustums.length === 0) {
      this.camerasFrustums = this.camerasCsv.map((camera, index) =>
        this.createFrustumForCamera(camera, index)
      );
    }
    document.addEventListener("click", this.selectPointOnModel, false);
    if (this.workerInitialized === null) this.initializeCameraWorkers();

    this.changeCursorToPointer();
  } else {
    this.useLimitiedGalleryMode(imagesCount);
  }
};

export const runCompletenessTest = function (imagesCount) {
  if (this.cameraSource === "transform") {
    return (this.cameraTransforms?.length ?? 0) === imagesCount;
  }
  return this.camerasCsv.length === imagesCount;
};

export const useLimitiedGalleryMode = function (imagesCount) {
  this.restoreDefaultCursor();
  if (this.galleryWarningShown) return;
  ElMessage.warning({
    message: this.$t("imageNavigation.limitedGalleryMode"),
    showClose: true,
    duration: 0,
  });
  this.galleryWarningShown = true;

  if (!TEST_COMPANIES.includes(this.companyId)) {
    trackEvent("limited_gallery_mode", {
      project_id: this.project.id,
      company_id: this.company.id,
      anonymous_user: this.anonymousUser,
      customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      reason: this.csvAvailable ? "completeness_test_failed" : "csv_missing",
      images_count: imagesCount,
      cameras_count: this.csvAvailable ? this.camerasCsv.length : 0,
    });
  }
};

export const selectPointOnModel = async function (event) {
  event.preventDefault();

  if (this.disableClick(event)) return;

  this.mousePositionOnScreen = {
    x: event.clientX,
    y: event.clientY,
  };

  this.setMousePosition(event);

  // Remove previous gallery point more thoroughly
  if (this.galleryPoint) {
    this.removeGalleryPoint();
  }

  const intersects = this.raycaster.intersectObject(
    this.modelObject.children[0]
  );

  if (intersects.length < 1) {
    this.resetBestImage();
    return;
  }

  this.changeCursorToLoading();
  this.galleryPointsSelected++;

  const position = intersects[0].point;

  this.galleryPoint = this.addImageMarkerToScene(position);

  const camerasIncludingPoint = this.getCamerasIncludingPoint(position);

  const bestFitCamera = await this.getBestFitCamera(
    camerasIncludingPoint,
    position
  );

  if (!bestFitCamera) {
    this.resetBestImage();
    this.restoreDefaultCursor();
    return;
  }

  // reset bestImage value to fire the watcher
  this.bestImage = null;
  this.bestImageBasename = null;
  this.$nextTick(() => {
    this.bestImage = bestFitCamera.originalIndex;
    this.bestImageBasename = bestFitCamera.basename ?? null;
    this.restoreDefaultCursor();
  });

  return bestFitCamera;
};

export const getCamerasIncludingPoint = function (point) {
  return this.camerasFrustums.filter((camera) =>
    camera.frustum.containsPoint(point)
  );
};

export const getBestFitCamera = async function (cameras, point) {
  if (!cameras || cameras.length === 0) {
    return;
  }

  await this.getCamerasWithClearLineOfSight(cameras, point);

  let candidateCameras = cameras.filter((camera) => camera.hasClearLineOfSight);

  this.calculateNormalizedDistanceToPoint(cameras);

  // Project the 3D point onto each candidate's image plane (applying lens
  // distortion). This is the ground truth for "does this image actually
  // contain the point" — frustum.containsPoint() is only a 3D approximation
  // that doesn't account for distortion or for the image's actual 2D bounds.
  candidateCameras.forEach((camera) => {
    camera.coordinatesOnImage = this.getPointCoordinatesOnImage(camera, point);
  });

  // Drop candidates where the projection landed outside the image. Without
  // this, a close-but-oblique camera (projection at e.g. {x: 1.15, y: 0.4})
  // can outscore a farther camera that genuinely frames the point, because
  // the distance-to-camera half of the score keeps it competitive.
  candidateCameras = candidateCameras.filter((camera) => {
    const c = camera.coordinatesOnImage;
    return c != null && c.x >= 0 && c.x <= 1 && c.y >= 0 && c.y <= 1;
  });

  this.bestImagesPoints = candidateCameras;

  if (candidateCameras.length === 0) {
    return;
  }

  candidateCameras.forEach((camera) => {
    camera.normalizedDistanceToCenter = this.calculateDistanceFromCenter(
      camera.coordinatesOnImage
    );
    camera.score = this.calculateBestFitScore(camera);
  });

  const bestFitCamera = candidateCameras.reduce(
    (best, camera) => (camera.score > best.score ? camera : best),
    candidateCameras[0]
  );

  return bestFitCamera;
};

export const getCamerasWithClearLineOfSight = function (cameras, point) {
  return new Promise((resolve) => {
    const allWorkersReady = this.workerInitialized.every((ready) => ready);
    if (!allWorkersReady) {
      setTimeout(() => {
        this.getCamerasWithClearLineOfSight(cameras, point).then(resolve);
      }, 100);
      // Without this return the function would fall through, post a batch to
      // workers that aren't ready yet, AND schedule a retry that posts a
      // second batch — the duplicate messages then write into a workerResults
      // that the first batch already nulled, crashing in handleCameraWorkerResults.
      return;
    }

    const workerCount = this.cameraWorkers.length;

    const chunkSize = Math.ceil(cameras.length / workerCount);
    const chunks = [];

    for (let i = 0; i < workerCount; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, cameras.length);
      if (start < cameras.length) {
        chunks.push(cameras.slice(start, end));
      }
    }

    this.workerResults = new Array(chunks.length).fill(null);
    this.totalWorkers = chunks.length;
    this.lineOfSightResolve = resolve;
    this.isProcessingLineOfSight = true;

    chunks.forEach((chunk, workerIndex) => {
      const cameraData = chunk.map((camera) => ({
        originalIndex: camera.originalIndex,
        position: {
          x: camera.virtualCamera.position.x,
          y: camera.virtualCamera.position.y,
          z: camera.virtualCamera.position.z,
        },
      }));

      this.cameraWorkers[workerIndex].postMessage({
        type: "CHECK_CAMERAS_LINE_OF_SIGHT",
        data: {
          cameras: cameraData,
          point: {
            x: point.x,
            y: point.y,
            z: point.z,
          },
        },
      });
    });
  });
};

export const getPointCoordinatesOnImage = function (camera, point) {
  const image = document.querySelector(`.image-thumbnail`);
  if (!image) throw new Error("No image found");

  const intrinsics = this.computeFullIntrinsics(
    camera.cameraData,
    this.originalImageWidth,
    this.originalImageHeight
  );
  const pointInPixel = this.projectWithDistortion(
    point,
    camera.virtualCamera,
    intrinsics
  );

  return pointInPixel;
};

export const calculateOffsetForFullScreen = function (image) {
  const contentBox = image.getBoundingClientRect();
  const renderedSize = this.getImageRenderedSize(image);

  const offsetX = (contentBox.width - renderedSize.width) / 2;
  const offsetY = (contentBox.height - renderedSize.height) / 2;

  return {
    offsetX,
    offsetY,
  };
};

export const calculateDistanceFromCenter = function (pointCoordinates) {
  const image = document.querySelector(".image-thumbnail");
  if (!image) throw new Error("No image found");

  const imageWidth = image.clientWidth;
  const imageHeight = image.clientHeight;
  const imageCenter = {
    x: imageWidth / 2,
    y: imageHeight / 2,
  };

  const distanceToCenter = Math.sqrt(
    Math.pow(pointCoordinates.x * imageWidth - imageCenter.x, 2) +
      Math.pow(pointCoordinates.y * imageHeight - imageCenter.y, 2)
  );

  const normalizedDistance =
    distanceToCenter /
    Math.sqrt(Math.pow(imageWidth / 2, 2) + Math.pow(imageHeight / 2, 2));

  return normalizedDistance;
};

export const calculateNormalizedDistanceToPoint = function (cameras) {
  const maxDistanceToPoint = cameras.reduce(
    (max, camera) => Math.max(max, camera.distanceToPoint),
    0
  );

  cameras.forEach((camera) => {
    camera.normalizedDistanceToPoint =
      maxDistanceToPoint === 0
        ? 0
        : camera.distanceToPoint / maxDistanceToPoint;
  });
};

export const calculateBestFitScore = function (camera) {
  const { normalizedDistanceToCenter, normalizedDistanceToPoint } = camera;

  const centerScore = 1 - normalizedDistanceToCenter;
  const distanceScore = 1 - normalizedDistanceToPoint;

  // 70/30 split favouring centeredness — a perfectly-framed point on a
  // slightly-further camera beats an oblique close-up.
  return 0.7 * centerScore + 0.3 * distanceScore;
};

export const createFrustumFromTransform = function (row, imageIndex) {
  const m = new THREE.Matrix4()
    .fromArray([].concat(...row.transformationMatrix))
    .transpose();


  let axisRot = this.exportedManually ? Math.PI / 2 : -Math.PI / 2;
  if (TEST_MANAUL_UPLOAD_PROJECTS.includes(this.projectId)) axisRot = 0;

  const axisFix = new THREE.Matrix4().makeRotationX(axisRot);
  const modelMatrix = this.modelObject.children[0].matrixWorld;
  // worldPose = modelMatrix · Rx · transform_matrix
  const c2w = new THREE.Matrix4()
    .multiplyMatrices(axisFix, m)
    .premultiply(modelMatrix);

  const position = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  c2w.decompose(position, quat, scale);

  const aspect = row.w / row.h;
  const fov = THREE.MathUtils.radToDeg(2 * Math.atan(row.h / (2 * row.flY)));
  const virtualCamera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 100);
  virtualCamera.position.copy(position);
  virtualCamera.quaternion.copy(quat);
  virtualCamera.updateMatrixWorld(true);
  virtualCamera.updateProjectionMatrix();

  const projMatrix = new THREE.Matrix4().multiplyMatrices(
    virtualCamera.projectionMatrix,
    virtualCamera.matrixWorldInverse
  );
  const frustum = new THREE.Frustum().setFromProjectionMatrix(projMatrix);

  return {
    frustum,
    virtualCamera,
    cameraData: {
      fx: row.flX,
      fy: row.flY,
      cx: row.cx,
      cy: row.cy,
      k1: row.k1,
      k2: row.k2,
      k3: row.k3,
      w: row.w,
      h: row.h,
    },
    originalIndex: imageIndex,
    basename: row.projectFile?.filename ?? row.name ?? null,
    projectFileId: row.projectFileId ?? row.projectFile?.pk ?? null,
  };
};

export const createFrustumForCamera = function (camera, index) {
  const worldMatrix = this.transformCamera(camera);

  // 2. Decompose the matrix into its position, quaternion, and scale components.
  const worldPosition = new THREE.Vector3();
  const worldQuaternion = new THREE.Quaternion();
  const worldScale = new THREE.Vector3(); // We don't need scale for the camera, but decompose extracts it.

  worldMatrix.decompose(worldPosition, worldQuaternion, worldScale);
  return {
    ...this.getFrustumFromCamera(camera, index),
    originalIndex: index,
  };
};

export const getFrustumFromCamera = function (
  camera,
  index,
  near = 0.1,
  far = 100
) {
  const image = document.querySelector(".image-thumbnail");
  if (!image) throw new Error("No image found");

  const { f, pitch, roll, x, y, alt } = camera;
  const heading = this.getCameraHeading(camera);

  const aspect = this.calculateAspectRatio(
    this.originalImageWidth,
    this.originalImageHeight
  );

  const fov = this.calculateFov(f, aspect);

  const relativePosition = new THREE.Vector3(x, y, alt);

  let defaultXRotation = this.exportedManually ? -Math.PI / 2 : Math.PI / 2;
  if (TEST_MANAUL_UPLOAD_PROJECTS.includes(this.projectId))
    defaultXRotation = 0;

  const rotationMatrix = new THREE.Matrix4().makeRotationX(defaultXRotation);
  relativePosition.applyMatrix4(rotationMatrix);

  const modelMatrix = this.modelObject.children[0].matrixWorld;
  const finalPosition = relativePosition.clone().applyMatrix4(modelMatrix);

  const pitchRadians = THREE.MathUtils.degToRad(pitch);
  const headingRadians = THREE.MathUtils.degToRad(heading);
  const rollRadians = THREE.MathUtils.degToRad(roll);

  const cameraEuler = new THREE.Euler(
    pitchRadians,
    rollRadians,
    -headingRadians,
    "ZXY"
  );

  const cameraQuaternion = new THREE.Quaternion().setFromEuler(cameraEuler);

  const modelQuaternion = new THREE.Quaternion();
  this.modelObject.children[0].getWorldQuaternion(modelQuaternion);

  const xRotationQuaternion = new THREE.Quaternion();
  xRotationQuaternion.setFromAxisAngle(
    new THREE.Vector3(1, 0, 0),
    defaultXRotation
  );

  const finalQuaternion = new THREE.Quaternion()
    .copy(modelQuaternion)
    .multiply(xRotationQuaternion)
    .multiply(cameraQuaternion);

  const virtualCamera = new THREE.PerspectiveCamera(fov, aspect, near, far);
  virtualCamera.position.copy(finalPosition);
  virtualCamera.quaternion.copy(finalQuaternion);

  virtualCamera.updateMatrixWorld(true);
  virtualCamera.updateProjectionMatrix();

  const frustum = new THREE.Frustum();
  const matrix = new THREE.Matrix4().multiplyMatrices(
    virtualCamera.projectionMatrix,
    virtualCamera.matrixWorldInverse
  );
  frustum.setFromProjectionMatrix(matrix);

  return { frustum, virtualCamera, cameraData: camera };
};

export const visualizeVirtualCamera = function (virtualCamera) {
  const helper = new THREE.CameraHelper(virtualCamera);
  this.scene.add(helper);
};

export const calculateFov = function (focalLength35mm) {
  const fovY = 2 * Math.atan(SENSOR_SIZE.FILM_HEIGHT / (2 * focalLength35mm));
  return THREE.MathUtils.radToDeg(fovY);
};

export const calculateAspectRatio = function (imageWidth, imageHeight) {
  return imageWidth / imageHeight;
};

export const calculateCameraQuaternion = function (pitch, heading, roll) {
  const pitchRadians = THREE.MathUtils.degToRad(pitch);
  const headingRadians = THREE.MathUtils.degToRad(heading);
  const rollRadians = THREE.MathUtils.degToRad(roll);

  return new THREE.Quaternion().setFromEuler(
    new THREE.Euler(pitchRadians, rollRadians, -headingRadians, "ZXY")
  );
};

export const removeGalleryPoint = function () {
  if (this.galleryPoint) {
    this.removeObjectFromScene(this.galleryPoint);
    this.galleryPoint = null;
  }
};

export const toggleFullScreenActive = function (value) {
  this.fullScreenImageActive = value;

  if (value) {
    this.renderer.setAnimationLoop(null);
  } else {
    this.renderer.setAnimationLoop(() => {
      this.renderer.render(this.scene, this.camera);

      if (this.controls && this.controls.update) this.controls.update();
    });
  }
};

export const resetBestImage = function () {
  this.bestImagesPoints = [];
  this.bestImage = null;
};

export const addImageMarkerToScene = function (position) {
  // Create DOM element for the pinpoint icon
  const pinpointIcon = document.createElement("img");
  pinpointIcon.src = pinpointSvg;

  // Use fixed size - no dynamic scaling
  const iconSize = 24; // Fixed size in pixels

  pinpointIcon.style = `width: ${iconSize}px; height: ${iconSize}px; pointer-events: none;`;

  const pinpointDiv = document.createElement("div");
  pinpointDiv.style = "pointer-events: none;";
  pinpointDiv.append(pinpointIcon);

  // Create CSS2DObject for the pinpoint
  const pinpointLabel = new CSS2DObject(pinpointDiv);
  pinpointLabel.position.copy(position);
  pinpointLabel.layers.set(0);
  pinpointLabel.renderOrder = 9999999;

  this.scene.add(pinpointLabel);

  return pinpointLabel;
};

export const getImageRenderedSize = function (img) {
  const style = window.getComputedStyle(img);

  const renderedWidth =
    img.clientWidth -
    parseFloat(style.paddingLeft) -
    parseFloat(style.paddingRight);

  const renderedHeight =
    img.clientHeight -
    parseFloat(style.paddingTop) -
    parseFloat(style.paddingBottom);

  return {
    width: renderedWidth,
    height: renderedHeight,
  };
};

export const computeFullIntrinsics = function (
  cameraData,
  imageWidth,
  imageHeight
) {
  // JSON path: intrinsics are already in pixels, return as-is.
  if (cameraData.fx != null) {
    const { fx, fy, cx, cy, k1, k2, k3, w, h } = cameraData;
    return { fx, fy, cx, cy, k1, k2, k3, w, h };
  }

  const { f, px, py, k1, k2, k3 } = cameraData;

  const pixelsPerMmX = imageWidth / SENSOR_SIZE.FILM_WIDTH;
  const pixelsPerMmY = imageHeight / SENSOR_SIZE.FILM_HEIGHT;

  const fx = f * pixelsPerMmX;
  const fy = f * pixelsPerMmY;

  const cx = imageWidth / 2 + px * pixelsPerMmX;
  const cy = imageHeight / 2 - py * pixelsPerMmY;

  // CSV path: dimensions come from the gallery's single global image size.
  return { fx, fy, cx, cy, k1, k2, k3, w: imageWidth, h: imageHeight };
};

export const projectWithDistortion = function (
  point3D,
  virtualCamera,
  intrinsics
) {
  const pointInCameraSpace = point3D
    .clone()
    .applyMatrix4(virtualCamera.matrixWorldInverse);

  if (pointInCameraSpace.z >= 0) return null;

  const x3 = pointInCameraSpace.x;
  const y3 = -pointInCameraSpace.y;
  const z3 = -pointInCameraSpace.z;

  const x_n = x3 / z3;
  const y_n = y3 / z3;

  const r2 = x_n * x_n + y_n * y_n;
  const r4 = r2 * r2;
  const r6 = r4 * r2;

  const distortionFactor =
    1.0 + intrinsics.k1 * r2 + intrinsics.k2 * r4 + intrinsics.k3 * r6;

  const x_distorted = x_n * distortionFactor;
  const y_distorted = y_n * distortionFactor;

  const final_x = intrinsics.fx * x_distorted + intrinsics.cx;
  const final_y = intrinsics.fy * y_distorted + intrinsics.cy;


  const w = intrinsics.w || this.originalImageWidth;
  const h = intrinsics.h || this.originalImageHeight;

  return new THREE.Vector2(final_x / w, final_y / h);
};

export const initializeCameraWorkers = function () {
  if (this.cameraWorkers.length > 0) {
    this.cameraWorkers.forEach((worker) => worker.terminate());
  }

  const workerCount = 4;
  this.workerInitialized = new Array(workerCount).fill(false);

  for (let i = 0; i < workerCount; i++) {
    const cameraWorker = new Worker(
      new URL("../workers/camera-worker.js", import.meta.url),
      { type: "module" }
    );

    cameraWorker.onmessage = (e) => {
      const { type, data, workerId } = e.data;

      switch (type) {
        case "INIT_COMPLETE":
          this.workerInitialized[workerId] = true;
          break;
        case "COMPLETE":
          this.handleCameraWorkerResults(data, workerId);
          break;
      }
    };

    this.cameraWorkers.push(cameraWorker);
  }

  this.initCameraWorkers();
};

export const initCameraWorkers = function () {
  const tolerance = this.calculateDynamicTolerance();

  const model = this.modelObject.children[0];
  const geometry = model.geometry;
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;

  this.cameraWorkers.forEach((worker, index) => {
    worker.postMessage({
      type: "INIT",
      workerId: index,
      data: {
        modelData: {
          positions: positions,
          indices: indices,
          rotation: {
            x: this.project.orientation.x,
            y: this.project.orientation.y,
            z: this.project.orientation.z,
          },
          scale: this.project.scale,
          tolerance,
        },
      },
    });
  });
};

export const handleCameraWorkerResults = function (results, workerId) {
  this.workerResults[workerId] = results;
  this.completedWorkers++;

  if (this.completedWorkers === this.totalWorkers) {
    const allResults = this.workerResults.flat();

    allResults.forEach((result) => {
      const camera = this.camerasFrustums.find(
        (cam) => cam.originalIndex === result.index
      );
      if (camera) {
        camera.hasClearLineOfSight = result.hasClearLineOfSight;
        camera.distanceToPoint = result.distanceToPoint;
      }
    });

    this.workerResults = null;
    this.completedWorkers = 0;
    this.totalWorkers = 0;

    this.isProcessingLineOfSight = false;
    this.lineOfSightResolve();
    this.lineOfSightResolve = null;
  }
};

export const calculateDynamicTolerance = function () {
  const bbox = new THREE.Box3().setFromObject(this.modelObject.children[0]);
  return bbox.getSize(new THREE.Vector3()).length() * 0.001;
};

/**
 * Inverse of projectWithDistortion.
 * Takes normalised image coordinates (0–1) for a given camera frustum and
 * returns a THREE.Ray in world space that passes through that pixel.
 */
export const unprojectPixelToRay = function (camera, normX, normY) {
  const intrinsics = this.computeFullIntrinsics(
    camera.cameraData,
    this.originalImageWidth,
    this.originalImageHeight
  );


  const w = intrinsics.w || this.originalImageWidth;
  const h = intrinsics.h || this.originalImageHeight;

  const pixelX = normX * w;
  const pixelY = normY * h;

  // Pixel → distorted normalised coords
  const x_d = (pixelX - intrinsics.cx) / intrinsics.fx;
  const y_d = (pixelY - intrinsics.cy) / intrinsics.fy;

  // Iterative fixed-point undistortion (converges in ~10 iterations for typical k values)
  let x_n = x_d;
  let y_n = y_d;
  for (let i = 0; i < 20; i++) {
    const r2 = x_n * x_n + y_n * y_n;
    const r4 = r2 * r2;
    const r6 = r4 * r2;
    const factor =
      1 + intrinsics.k1 * r2 + intrinsics.k2 * r4 + intrinsics.k3 * r6;
    x_n = x_d / factor;
    y_n = y_d / factor;
  }

  // Ray direction in camera space: (x_n, -y_n, -1)
  // Matches the sign convention used in projectWithDistortion
  // where y3 = -pointInCameraSpace.y and z3 = -pointInCameraSpace.z
  const rayDirCamera = new THREE.Vector3(x_n, -y_n, -1).normalize();

  // Rotate to world space (translation-free)
  const rayDirWorld = rayDirCamera
    .clone()
    .transformDirection(camera.virtualCamera.matrixWorld);

  return new THREE.Ray(camera.virtualCamera.position.clone(), rayDirWorld);
};

/**
 * Lazily builds the camera frustums required to back-project annotation
 * markers onto the model without opening the gallery. Call this only when
 * there are annotations to display — it triggers a cameras fetch.
 *
 * No-op if frustums are already built. Source-aware: "transform" uses the
 * CameraTransform rows, "csv" uses the legacy Camera/CSV rows.
 */
export const ensureCameraFrustumsForAnnotations = async function () {
  if (this.camerasFrustums.length > 0) return;

  await this.setupCameraMesh();

  if (this.cameraSource === "transform") {
    this.camerasFrustums = this.cameraTransforms.map((row, index) =>
      this.createFrustumFromTransform(row, index)
    );
    // unprojectPixelToRay converts normalised → pixel coordinates using
    // originalImageWidth/Height, which the gallery only sets once opened.
    // Transform rows carry the source image dimensions, so seed them here.
    if (!this.originalImageWidth && this.cameraTransforms[0]) {
      this.originalImageWidth = this.cameraTransforms[0].w;
      this.originalImageHeight = this.cameraTransforms[0].h;
    }
  } else if (this.camerasCsv.length > 0) {
    // Legacy CSV frustums depend on gallery DOM (.image-thumbnail) and
    // originalImageWidth, which aren't available before the gallery opens —
    // those markers will be projected when the gallery is first opened.
    this.camerasFrustums = this.camerasCsv.map((camera, index) =>
      this.createFrustumForCamera(camera, index)
    );
  }
};

/**
 * Back-projects the centre of an annotation rectangle (given as normalised
 * image coordinates 0–1) onto the 3-D model and places the gallery pinpoint
 * marker at the intersection point.
 *
 * @param {number} cameraIndex  Index into camerasFrustums (== selectedImageIndex)
 * @param {number} normX        Normalised x coordinate of the annotation centre
 * @param {number} normY        Normalised y coordinate of the annotation centre
 */
export const addAnnotationMarkerToModel = function (cameraIndex, normX, normY, annotationData) {
  const camera = this.camerasFrustums?.[cameraIndex];
  if (!camera || !this.modelObject?.children?.[0]) return;

  if (!this.annotationMarkers) this.annotationMarkers = [];

  // Skip if a marker for this annotation already exists. Markers are projected
  // from two paths (model load and gallery open); without this guard each
  // annotation gets two markers, producing gaps in the numbering (1, 3, 5…).
  const annotationId = annotationData?.id;
  if (
    annotationId != null &&
    this.annotationMarkers.some((m) => m._annotationClickData?.annotationId == annotationId)
  ) {
    return;
  }

  const ray = this.unprojectPixelToRay(camera, normX, normY);

  this.raycaster.set(ray.origin, ray.direction);
  const intersects = this.raycaster.intersectObject(this.modelObject.children[0]);
  if (intersects.length === 0) return;

  const markerNumber = this.annotationMarkers.length + 1;

  const markerDiv = document.createElement("div");
  markerDiv.style.cssText = "pointer-events:auto; cursor:pointer;";
  markerDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <g transform="translate(0, 0)">
      <path
        d="M36.7992 15.9992C36.7992 23.988 27.9368 32.308 24.9608 34.8776C24.6836 35.0861 24.3461 35.1988 23.9992 35.1988C23.6523 35.1988 23.3149 35.0861 23.0376 34.8776C20.0616 32.308 11.1992 23.988 11.1992 15.9992C11.1992 12.6044 12.5478 9.34872 14.9483 6.94825C17.3487 4.54779 20.6044 3.19922 23.9992 3.19922C27.394 3.19922 30.6497 4.54779 33.0502 6.94825C35.4507 9.34872 36.7992 12.6044 36.7992 15.9992Z"
        fill="white" stroke="#FA6930" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"
      />
      <text x="24" y="21" text-anchor="middle" dominant-baseline="middle" font-size="13" font-weight="700" fill="#FA6930" font-family="sans-serif">${markerNumber}</text>
      <circle cx="24.2" cy="41.2" r="3.2" fill="white"/>
    </g>
  </svg>`;

  const markerLabel = new CSS2DObject(markerDiv);
  // A new annotation is not in allAnnotations yet — its marker stays visible
  // and the sidebar un-hides the whole section right after.
  const isExistingAnnotation = this.allAnnotations?.some(
    (a) => a.id == annotationId
  );
  markerLabel.visible = !(this.annotationMarkersHidden && isExistingAnnotation);
  markerLabel.position.copy(intersects[0].point);
  markerLabel.layers.set(0);
  markerLabel.renderOrder = 9999999;
  markerLabel._annotationClickData = { cameraIndex, annotationId: annotationData?.id };

  markerDiv.addEventListener("click", (e) => {
    e.stopPropagation();
    this.openAnnotationInGallery(cameraIndex, annotationData?.id);
  });

  this.scene.add(markerLabel);

  this.annotationMarkers.push(markRaw(markerLabel));
};

/**
 * Removes the 3-D marker for a deleted annotation from the scene and renumbers
 * the remaining markers so their labels stay sequential (no gaps).
 *
 * @param {number|string} annotationId  Id of the annotation that was deleted
 */
export const removeAnnotationMarkerFromModel = function (annotationId) {
  if (!this.annotationMarkers || annotationId == null) return;

  this.annotationMarkers = this.annotationMarkers.filter((marker) => {
    const isMatch = marker._annotationClickData?.annotationId == annotationId;
    if (isMatch) {
      this.scene.remove(marker);
      // The CSS2DObject's DOM element is appended by the labelRenderer; drop it
      // so it doesn't linger in the overlay after the object leaves the scene.
      marker.element?.remove();
    }
    return !isMatch;
  });

  this.annotationMarkers.forEach((marker, i) => {
    const textEl = marker.element?.querySelector("text");
    if (textEl) textEl.textContent = String(i + 1);
  });
};

export const setThumbnailsAvailable = function (images) {
  this.thumbnailsAvailable = images.every((image) => image.thumbnailAvailable);

  return this.thumbnailsAvailable;
};
